/**
 * Needs attention's page words and queues (v1 completion I §10, Ben's copy,
 * d73). The kinds' labels and the queues are the server's (the object
 * manifest declares them for `needs_attention.alert`); a unit test pins these
 * to the generated manifest so the Classic page cannot drift from One's.
 */
import type {
  AlertKind,
  NeedsAttentionListOptions,
  NeedsAttentionQueue,
} from "../data/needs-attention-types";
import { ApiError } from "../data/api/errors";
import { currentTime } from "./current-time";

export const NEEDS_ATTENTION_QUEUES: readonly {
  id: NeedsAttentionQueue;
  label: string;
  options: NeedsAttentionListOptions;
}[] = [
  { id: "mine", label: "Mine", options: { mine: true } },
  {
    id: "my_departments",
    label: "My departments",
    options: { myDepartments: true },
  },
  { id: "past_due", label: "Past due", options: { isOverdue: true } },
  { id: "all", label: "All", options: {} },
];

/** The mock-up's six kinds, in its order, then the stock price rule's (the server maps each to the rules' groups). */
export const ALERT_KIND_LABELS: Record<AlertKind, string> = {
  waiting_on_customer: "Waiting on the customer",
  release: "Release",
  purchase_orders: "Purchase orders",
  delivery: "Delivery",
  payment_and_credit: "Payment and credit",
  made_line: "Made in-house",
  stock_prices: "Stock prices",
};

export const EMPTY_QUEUE = "Nothing needs attention.";
export const EMPTY_CSR_HOME_PANEL = "Nothing needs your attention.";

export function isNeedsAttentionQueue(
  value: string | null,
): value is NeedsAttentionQueue {
  return NEEDS_ATTENTION_QUEUES.some((queue) => queue.id === value);
}

/** The list options for a queue, with the page's filters on top. */
export function queueListOptions(
  queue: NeedsAttentionQueue,
  filters: Omit<
    NeedsAttentionListOptions,
    "mine" | "myDepartments" | "isOverdue"
  >,
): NeedsAttentionListOptions {
  const base =
    NEEDS_ATTENTION_QUEUES.find((q) => q.id === queue)?.options ?? {};
  return { ...filters, ...base };
}

/** "2 need attention", "1 needs attention": the order and purchase order pages' strip. */
export function needAttentionCount(count: number): string {
  return count === 1 ? "1 needs attention" : `${count} need attention`;
}

/** "last 09:40" today, "last Thu 16 Oct, 09:40" before (London); null before the first complete sweep. */
export function lastSweepWords(
  lastCompleteAt: string | null | undefined,
  now: Date = currentTime(),
): string | null {
  if (!lastCompleteAt) return null;
  const at = new Date(lastCompleteAt);
  if (Number.isNaN(at.getTime())) return null;
  const zone = { timeZone: "Europe/London" } as const;
  const time = at.toLocaleTimeString("en-GB", {
    ...zone,
    hour: "2-digit",
    minute: "2-digit",
  });
  const day = (d: Date) =>
    d.toLocaleDateString("en-GB", {
      ...zone,
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  return day(at) === day(now)
    ? `last ${time}`
    : `last ${day(at).replace(",", "")}, ${time}`;
}

/**
 * The server's words for a refusal: Needs attention's routes answer 404 and 409 with a plain-string `detail`
 * ("The alert has cleared, so the alert can't be changed."), and 400s with the canonical `{ message, errors }`
 * envelope. Null when the error is not the server's.
 */
export function refusalWords(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body as { detail?: unknown } | null;
  const detail = body?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === "string") return message;
  }
  return null;
}
